import React, { useEffect, useRef, useState } from "react";
import { IComponentParent, Nullable } from "sonobello.utilities.react";
import { TelemetrySeverity } from "sonobello.utilities.react.telemetry";

import EnvironmentConfiguration from "../../../../constants/EnvironmentConfiguration";
import LoadingExpander from "../../../App/Components/LoadingExpander";
import DateNow from "../../../Telemetry/Compositions/DateNow";
import TelemetryClient from "../../../Telemetry/Compositions/TelemetryClient";
import { TelemetrySessionTags } from "../../../Telemetry/Types/ITelemetryClient";
import ISchedulingLoaderProps from "../Types/ISchedulingLoaderProps";
import {
  SchedulingLoaderCompletedEvent,
  SchedulingLoaderDelinquentEvent,
  SchedulingLoaderPendingEvent,
  SchedulingLoaderState
} from "../Types/SchedulingLoaderTelemetryEvents";

export const TelemetryMessages = {
  completed: "Scheduling Step Loaded"
};

const getLoadingTimeMs = (startTime: number): number => DateNow.now() - startTime;

/** Shows a loading spinner until the booking controller constraints are met and traces while this process is pending
 * and when it completes.
 */
const ObservedSchedulingLoader: React.FC<ISchedulingLoaderProps & IComponentParent> = ({
  bookingController,
  customerFirstName,
  reservation,
  router,
  children
}) => {
  const [isLoading, setIsLoading] = useState(!bookingController?.selected);
  const isLoadingRef = useRef(isLoading);
  const intervalRef = useRef<Nullable<NodeJS.Timer>>(null);
  const startTimeRef = useRef(0);
  const bookingControllerRef = useRef(bookingController);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    setIsLoading(l => (!l ? l : !bookingController?.selected));
    bookingControllerRef.current = bookingController;
  }, [bookingController]);

  useEffect(() => {
    if (!isLoadingRef.current && !isLoading) return;
    isLoadingRef.current = isLoading;

    if (isLoading) {
      startTimeRef.current = DateNow.now();
      intervalRef.current = setInterval(() => {
        const elapsedTimeMs = getLoadingTimeMs(startTimeRef.current);
        if (elapsedTimeMs > EnvironmentConfiguration.timingsMs.schedulingLoaderTimeout) {
          TelemetryClient.traceEvent(
            new SchedulingLoaderDelinquentEvent(
              elapsedTimeMs,
              bookingControllerRef.current?.bookableCenters.length ?? 0,
              bookingControllerRef.current?.getForTelemetry() ?? null
            )
          );
          TelemetryClient.setContextTag(TelemetrySessionTags.SchedulingStepLoading, SchedulingLoaderState.Delinquent);
        } else {
          TelemetryClient.traceEvent(
            new SchedulingLoaderPendingEvent(
              elapsedTimeMs,
              bookingControllerRef.current?.bookableCenters.length ?? 0,
              bookingControllerRef.current?.getForTelemetry() ?? null
            )
          );
          TelemetryClient.setContextTag(TelemetrySessionTags.SchedulingStepLoading, SchedulingLoaderState.Pending);
        }
      }, EnvironmentConfiguration.timingsMs.schedulingLoaderHeartbeat);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      TelemetryClient.traceEvent(new SchedulingLoaderCompletedEvent(getLoadingTimeMs(startTimeRef.current)));
    }
  }, [isLoading]);

  useEffect(() => {
    if (!reservation && bookingController && !bookingController.isBookingPossible) {
      TelemetryClient.setContextTag(TelemetrySessionTags.SchedulingStepLoading, SchedulingLoaderState.Completed);
      TelemetryClient.traceMessage(TelemetryMessages.completed, TelemetrySeverity.Info);
      router.goToNoAvailabilityView({ customerFirstName });
    }
  }, [bookingController, customerFirstName]);

  return isLoading ? <LoadingExpander message="Loading the Calendar..." /> : <>{children}</>;
};

export default ObservedSchedulingLoader;
